import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { TextField } from '@mui/material';
import FilterListIcon from '@material-ui/icons/FilterList';
import { updateFilterOptions } from '../../redux/slices/clients';
import { RootState } from '../../redux/store';
import { periodReferences } from '../../utils/periodDate';
import { useDateRange } from '../../utils/useDateRange';
import 'moment/locale/pt-br';

moment.locale('pt-br');

const periodOptions = Object.values(periodReferences);

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

export function ClientsFilter() {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { reset } = useSelector((state: RootState) => state.global);
  const { isRequestingClients } = useSelector((state: RootState) => state.clients);
  const [filterPeriodRequesting, setFilterPeriodRequesting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialState = {
    period: 'ALL',
  };

  const [filter, setFilter] = useState(initialState);

  const isRangedDate = filter.period.split(':')[1];
  if (isRangedDate) {
    setFilter((state) => ({ ...state, period: 'BETWEEN_DATES' }));
  }

  const { valueFromDate, valueBeforeDate, periodDateRange, handleDateChange } = useDateRange();

  useEffect(() => {
    if (periodDateRange) {
      setFilter((state) => ({ ...state, period: 'BETWEEN_DATES' }));
    }
  }, [periodDateRange]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'period') {
      const periodValues = Object.values(periodReferences);
      const periodKeys = Object.keys(periodReferences);
      const foundIndexOf = periodValues.findIndex((item) => item === value);
      setFilter((state) => ({ ...state, [name]: periodKeys[foundIndexOf] }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFilterPeriodRequesting(true);
    if (filter.period === 'BETWEEN_DATES') filter.period = periodDateRange;
    dispatch(updateFilterOptions({ period: filter.period }));
  };

  useEffect(() => {
    if (reset === true) setFilter(initialState);
  }, [initialState, reset]);

  useEffect(() => {
    if (isRequestingClients === false) {
      setFilterPeriodRequesting(false);
    }
  }, [isRequestingClients]);

  return (
    <Box sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <form
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
        onSubmit={(event) => handleSubmit(event)}
      >
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="clients-period">Período</InputLabel>
          <Select
            labelId="period-label"
            id="period"
            name="period"
            value={periodOptions[Object.keys(periodReferences).indexOf(filter.period)] || ''}
            onChange={handleChange}
            label="Período"
          >
            {renderList(periodOptions)}
          </Select>
        </FormControl>

        {filter.period === 'BETWEEN_DATES' && (
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
            <Box sx={{ display: 'flex', gap: 2, mx: 1 }}>
              <DatePicker
                label="De"
                value={valueFromDate}
                onChange={(newValue) => handleDateChange(newValue, true)}
                renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="Até"
                value={valueBeforeDate}
                onChange={(newValue) => handleDateChange(newValue, false)}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          </LocalizationProvider>
        )}
        <Button
          type="submit"
          color="primary"
          variant="contained"
          startIcon={filterPeriodRequesting ? <CircularProgress size={20} /> : <FilterListIcon />}
          disabled={filterPeriodRequesting}
          style={{ marginTop: 16 }}
        >
          Filtrar Período
        </Button>
      </form>
    </Box>
  );
}

const renderList = (list) =>
  list.map((item, index) => (
    <MenuItem key={index} value={item}>
      {item}
    </MenuItem>
  ));
